import type {
  GridFilterItem,
  GridFilterOperator,
  GridStateColDef,
} from "@material-ui/x-grid";
import { GridFilterInputValue } from "@material-ui/x-grid";

import {
  calendarYmdInTimeZone,
  isYmdString,
  tryYmdStringToEndOfDayUtc,
  tryYmdStringToStartOfDayUtc,
} from "src/utils/timezone";

function asCellDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  return null;
}

/**
 * Filter value from `GridFilterInputValue` with `type="date"`: `yyyy-mm-dd` string,
 * or a `Date` (e.g. legacy). Maps that civil day into start/end UTC instants in
 * `timeZone` (same as MUI date operators, but zone-aware).
 */
function filterDayRangeFromFilterValue(
  filterValue: unknown,
  timeZone: string
): { start: Date; end: Date } | null {
  if (filterValue == null) return null;
  if (typeof filterValue === "string" && isYmdString(filterValue)) {
    const start = tryYmdStringToStartOfDayUtc(filterValue, timeZone);
    const end = tryYmdStringToEndOfDayUtc(filterValue, timeZone);
    if (!start || !end) return null;
    return { start, end };
  }
  if (filterValue instanceof Date) {
    if (isNaN(filterValue.getTime())) return null;
    const ymd = calendarYmdInTimeZone(filterValue, timeZone);
    if (!ymd) return null;
    const start = tryYmdStringToStartOfDayUtc(ymd, timeZone);
    const end = tryYmdStringToEndOfDayUtc(ymd, timeZone);
    if (!start || !end) return null;
    return { start, end };
  }
  return null;
}

/** Matches built-in `getGridDateOperators(false)` filter UI (native date input). */
const dateFilterInputProps = { type: "date" as const };

/**
 * Date column operators matching MUI X Grid, but filter values use civil
 * Y/M/D from the picker interpreted in `timeZone` (stored user zone).
 */
export function getZonedGridDateOperators(
  timeZone: string
): GridFilterOperator[] {
  return [
    {
      label: "is",
      value: "is",
      getApplyFilterFn: (
        filterItem: GridFilterItem,
        _column: GridStateColDef
      ) => {
        const range = filterDayRangeFromFilterValue(filterItem.value, timeZone);
        if (!range) return null;
        const { start, end } = range;
        return (params) => {
          const cv = asCellDate(params.value);
          if (!cv) return false;
          return cv >= start && cv <= end;
        };
      },
      InputComponent: GridFilterInputValue,
      InputComponentProps: dateFilterInputProps,
    },
    {
      label: "is not",
      value: "not",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        const range = filterDayRangeFromFilterValue(filterItem.value, timeZone);
        if (!range) return null;
        const { start, end } = range;
        return (params) => {
          const cv = asCellDate(params.value);
          if (!cv) return true;
          return cv < start || cv > end;
        };
      },
      InputComponent: GridFilterInputValue,
      InputComponentProps: dateFilterInputProps,
    },
    {
      label: "is after",
      value: "after",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        const range = filterDayRangeFromFilterValue(filterItem.value, timeZone);
        if (!range) return null;
        const { end } = range;
        return (params) => {
          const cv = asCellDate(params.value);
          if (!cv) return false;
          return cv > end;
        };
      },
      InputComponent: GridFilterInputValue,
      InputComponentProps: dateFilterInputProps,
    },
    {
      label: "is on or after",
      value: "onOrAfter",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        const range = filterDayRangeFromFilterValue(filterItem.value, timeZone);
        if (!range) return null;
        const { start } = range;
        return (params) => {
          const cv = asCellDate(params.value);
          if (!cv) return false;
          return cv >= start;
        };
      },
      InputComponent: GridFilterInputValue,
      InputComponentProps: dateFilterInputProps,
    },
    {
      label: "is before",
      value: "before",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        const range = filterDayRangeFromFilterValue(filterItem.value, timeZone);
        if (!range) return null;
        const { start } = range;
        return (params) => {
          const cv = asCellDate(params.value);
          if (!cv) return false;
          return cv < start;
        };
      },
      InputComponent: GridFilterInputValue,
      InputComponentProps: dateFilterInputProps,
    },
    {
      label: "is on or before",
      value: "onOrBefore",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        const range = filterDayRangeFromFilterValue(filterItem.value, timeZone);
        if (!range) return null;
        const { end } = range;
        return (params) => {
          const cv = asCellDate(params.value);
          if (!cv) return false;
          return cv <= end;
        };
      },
      InputComponent: GridFilterInputValue,
      InputComponentProps: dateFilterInputProps,
    },
    {
      label: "is empty",
      value: "isEmpty",
      getApplyFilterFn: () => {
        return (params) => params.value == null;
      },
    },
    {
      label: "is not empty",
      value: "isNotEmpty",
      getApplyFilterFn: () => {
        return (params) => params.value != null;
      },
    },
  ];
}
